import BulkUploadModal from '@components/Projects/BulkUpload/BulkUploadModal';
import { FolderGrid, type FolderSection, FolderTable } from '@components/Projects/FolderItems';
import { compareItems, type FolderSortKey } from '@components/Projects/folderSort';
import FolderModal from '@components/Projects/FolderModal';
import {
  type CustomFolderChildRow, folderRowsOf, isSameOrBelow, projectRowOf, projectRowsOf
} from '@components/Projects/folderRows';
import FolderTreePane from '@components/Projects/FolderTreePane';
import MoveDialog from '@components/Projects/MoveDialog';
import ShareDialog from '@components/Projects/ShareDialog';
import ProjectUploadModal from '@components/Projects/UploadModal';
import DeleteConfirmationDialog from '@components/Shared/Dialog/DeleteConfirmationDialog';
import Header from '@components/Shared/Layout/Header';
import {
  menuDangerItemStyles, menuItemStyles, menuPaperStyles, menuSubheaderStyles
} from '@components/Shared/Menu/menu.styles';
import SearchInput from '@components/Shared/SearchInput';
import { KEY } from '@constants/key';
import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import { buildFolderPath, buildProjectPath } from '@lib/routing/projectPath';
import { useProjectRoute } from '@lib/routing/useProjectRoute';
import {
  Box, Button, Divider, IconButton, LinearProgress, ListItemIcon, ListSubheader, Menu, MenuItem, Paper,
  type PopoverPosition, Tooltip, Typography
} from '@mui/material';
import {
  contentCountStyles, contentHeadStyles, contentPaneStyles, contentScrollStyles, emptyMessageStyles, loadingBarStyles,
  toolbarButtonStyles, toolbarGroupStyles, toolbarIconButtonStyles, toolbarStyles, treePaneStyles, windowBodyStyles,
  windowStyles
} from '@pages/Projects.styles';
import { apiSlice } from '@store/api/apiSlice';
import { isFolder, useDeleteFolderMutation, useGetFolderByPathQuery } from '@store/api/folderEndpoints';
import {
  folderPathOf,
  useAddProjectMutation, useDeleteProjectMutation, useGetProjectsQuery
} from '@store/api/projectEndpoints';
import { useAppDispatch } from '@store/hooks';
import { useDebounce } from '@store/hooks/useDebounce';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import {
  ArrowDownUpIcon, ArrowRightIcon, CheckIcon, ChevronDownIcon, FileUpIcon, FolderInputIcon,
  FolderPlusIcon, LayoutGridIcon, LinkIcon, ListIcon, PencilIcon, RefreshCwIcon, Share2Icon, SquareArrowOutUpRightIcon,
  Trash2Icon, UploadIcon
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createSearchParams, useMatch, useNavigate, useSearchParams } from 'react-router-dom';

export type { CustomFolderChildRow };

type FolderView = 'grid' | 'list';

/** Where the item menu opens: at the item, from the keyboard's menu key, or where a right-click or a long press was. */
type MenuAnchor = { element: HTMLElement } | { point: PopoverPosition };

const ProjectsPage = () => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  // Kept apart from `selectedItem`, which is cleared on close: the dialog still shows the
  // item while it animates out, and the add-folder modal reads `selectedItem` as "rename".
  const [moveItem, setMoveItem] = useState<CustomFolderChildRow | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor | null>(null);
  const [backgroundMenuAt, setBackgroundMenuAt] = useState<PopoverPosition | null>(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);
  const [viewMenuAnchor, setViewMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedItem, setSelectedItem] = useState<CustomFolderChildRow | null>();
  // The folder the selected item is in, which the listing and the tree both say: its rename is
  // checked against that folder's items, and its live share data is read from that folder.
  const [selectedParentPath, setSelectedParentPath] = useState<string>('/');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Remembered in this browser, like the report layout last picked.
  const [view, setView] = useState<FolderView>(() => localStorage.getItem(KEY.FOLDER_VIEW) === 'list' ? 'list' : 'grid');
  const [pickedSort, setPickedSort] = useState<FolderSortKey | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [addProject, { isLoading: isAddProjectLoading }] = useAddProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const [deleteFolder] = useDeleteFolderMutation();
  const { t, currentLanguage } = useLocalization();

  const isProjectsPage = useMatch(ROUTE_PATHS.PROJECTS);
  const { folderPath } = useProjectRoute();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const debouncedSearchTerm = useDebounce(search, 500);
  // `currentData`, not `data`: the page stays mounted from folder to folder, and `data` would
  // keep showing the folder that was left, clickable, until the next one arrives.
  const viewedPath = folderPath || '/';
  const { currentData: listedFolder, error: folderError, refetch: folderRefetch, isFetching: isFolderFetching }
    = useGetFolderByPathQuery(viewedPath, { skip: Boolean(debouncedSearchTerm) });
  const { currentData: projects, refetch: projectsRefetch, isFetching: isProjectsFetching }
    = useGetProjectsQuery({ search: debouncedSearchTerm }, { skip: !debouncedSearchTerm });

  const isSearchPending = Boolean(isProjectsPage) && search != debouncedSearchTerm;
  const isFetching = isFolderFetching || isProjectsFetching || isSearchPending;

  // A folder that is not there: renamed, moved or deleted in another tab, or a link to nothing.
  // What was read at this address before is not shown under it: its items and its permissions
  // belong to a folder that is no longer here.
  const isFolderMissing = !isProjectsPage
    && folderError !== undefined && 'status' in folderError && folderError.status === 404;
  const folder = isFolderMissing ? undefined : listedFolder;

  // The folder the selected item is in, as the tree or the listing read it: the same cache entry,
  // so it is current after any change made here.
  const { currentData: selectedParent } = useGetFolderByPathQuery(selectedParentPath, {
    skip: !selectedItem || selectedItem.type !== 'Folder'
  });

  // A folder in view, or below one, that this tab renames or moves is followed to where it went.
  // One changed in another tab is not: the page then says the folder is not found.
  const follow = (oldPath: string, newPath: string) => {
    if (isProjectsPage || !isSameOrBelow(viewedPath, oldPath)) return;
    navigate(buildFolderPath(newPath + viewedPath.slice(oldPath.length)), { replace: true });
  };

  const handleRenamed = (row: CustomFolderChildRow, name: string) => {
    if (row.type !== 'Folder') return;
    const { path } = row.item;
    follow(path, `${path.slice(0, path.lastIndexOf('/'))}/${name}`);
  };

  const handleMoved = (row: CustomFolderChildRow, destinationPath: string) => {
    if (row.type !== 'Folder') return;
    follow(row.item.path, `${destinationPath === '/' ? '' : destinationPath}/${row.name}`);
  };

  // The listing and every opened folder of the tree, read again: all of them are folders.
  const refreshFolders = () => {
    folderRefetch();
    dispatch(apiSlice.util.invalidateTags(['Folder']));
  };

  // Every row and the folder itself arrive with their own permission list, so both the
  // toolbar and the row actions read the answer straight off the resource.
  const canCreateFolder = has(folder, 'folder:post');
  const canUploadProject = has(folder, 'project:post') && has(folder, 'inspection:post');

  const selectedResource = selectedItem?.item;

  const canEditSelected = Boolean(selectedItem) && (selectedItem?.type === 'Folder'
    ? has(selectedResource, 'folder:put')
    : has(selectedResource, 'project:patch'));
  const canDeleteSelected = Boolean(selectedItem) && (selectedItem?.type === 'Folder'
    ? has(selectedResource, 'folder:delete')
    : has(selectedResource, 'project:delete'));
  const canShareSelected = selectedItem?.type === 'Folder' && has(selectedResource, 'folder:share');

  // The share dialog reads live share data, so resolve the selected folder from the query cache.
  const shareTargetFolder = selectedItem?.type === 'Folder'
    ? selectedParent?.children.filter(isFolder).find((child) => child.item._id === selectedItem.id)?.item
    ?? selectedItem.item
    : null;

  const disabledActionTooltip = t('Permission_NotAllowed');

  useEffect(() => {
    if (search) {
      navigate({
        pathname: ROUTE_PATHS.PROJECTS,
        search: createSearchParams({ search: search }).toString()
      });

    } else {
      if (isProjectsPage) navigate({ pathname: ROUTE_PATHS.FOLDERS });
    }

  }, [isProjectsPage, navigate, searchParams, search]);

  const changeView = (next: FolderView) => {
    setView(next);
    localStorage.setItem(KEY.FOLDER_VIEW, next);
  };

  // Search results keep the order the search ranked them in until a column is picked; a
  // folder lists by name.
  const sort: FolderSortKey | null = pickedSort ?? (isProjectsPage ? null : 'name');

  // An item of the listing is in the folder in view, or, in search results, in its own.
  const actionMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: CustomFolderChildRow,
    parentPath: string = (row.type === 'Project' && folderPathOf(row.item)) || viewedPath
  ) => {
    event.stopPropagation();
    event.preventDefault();

    // A right-click opens the menu where it was. The keyboard's menu key gives no position,
    // so the menu then opens at the item.
    setMenuAnchor(event.type === 'contextmenu' && (event.clientX || event.clientY)
      ? { point: { top: event.clientY, left: event.clientX } }
      : { element: event.currentTarget });
    setSelectedItem(row);
    setSelectedParentPath(parentPath);
  };

  const actionMenuClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    unfocusProject(!!reason);
  };

  const unfocusProject = (cleanSelectedProject: boolean = false) => {
    setMenuAnchor(null);
    if (cleanSelectedProject) setSelectedItem(null);
  };

  // A right-click on the empty part of the pane offers what the toolbar does, and how the
  // listing is shown and sorted.
  const backgroundMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    // The folder modal reads a selected item as the folder to rename.
    setSelectedItem(null);
    setBackgroundMenuAt({ top: event.clientY, left: event.clientX });
  };

  // A right-click while a menu is open lands on its backdrop: it closes the menu rather than
  // opening the browser's own over it.
  const closeOnRightClick = (event: React.MouseEvent) => {
    event.preventDefault();
    unfocusProject(true);
    setBackgroundMenuAt(null);
  };

  // Where an item opens: in this tab, in a new one, or as a link copied from the menu.
  const pathOf = (row: CustomFolderChildRow) => {
    if (row.type === 'Project') {
      // A project's URL carries the folder it lives in. In a listing that is the folder in
      // view; in search results the rows come from all over the tree and carry their own.
      const rowFolderPath = folderPathOf(row.item) || viewedPath;

      return buildProjectPath({ folderPath: rowFolderPath, projectId: row.id });
    }
    return buildFolderPath(row.item.path);
  };

  const openItem = (row: CustomFolderChildRow) => navigate(pathOf(row));

  const handleOpen = () => {
    if (selectedItem) openItem(selectedItem);
    unfocusProject(true);
  };

  const handleOpenInNewTab = () => {
    if (selectedItem) window.open(pathOf(selectedItem), '_blank', 'noopener');
    unfocusProject(true);
  };

  const handleCopyLink = async () => {
    unfocusProject(true);
    if (!selectedItem) return;

    await navigator.clipboard.writeText(window.location.origin + pathOf(selectedItem));
    dispatch(showSnackbar({ severity: 'success', message: t('Common_Copied') }));
  };

  const handleRename = () => {
    setFolderModalOpen(true);
    unfocusProject();
  };

  const handleMove = () => {
    setMoveItem(selectedItem ?? null);
    setMoveDialogOpen(true);
    unfocusProject(true);
  };

  const handleShare = () => {
    setShareDialogOpen(true);
    unfocusProject();
  };

  const handleShareCanceled = () => {
    setShareDialogOpen(false);
    setSelectedItem(null);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    unfocusProject();
  };

  const handleDeleteConfirmation = async () => {
    if (selectedItem) {
      if (selectedItem.type === 'Project') await deleteProject(selectedItem.id);
      if (selectedItem.type === 'Folder') {
        // A folder deleted from the tree can be the one in view or above it. Once it is gone the
        // folder it was in opens: the tree's, which for one another team shared is the root, as
        // its real parent is not the reader's to open.
        const result = await deleteFolder(selectedItem.item);
        if (!result.error && !isProjectsPage && isSameOrBelow(viewedPath, selectedItem.item.path)) {
          navigate(buildFolderPath(selectedParentPath), { replace: true });
        }
      }
    }
    setSelectedItem(null);
  };

  const handleDeleteCanceled = async () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const handleFolderCanceled = async () => {
    setFolderModalOpen(false);
    setSelectedItem(null);
  };

  const handleNewFolder = () => {
    setBackgroundMenuAt(null);
    setFolderModalOpen(true);
  };

  const handleUpload = () => {
    setBackgroundMenuAt(null);
    setUploadModalOpen(true);
  };

  // The view and the sorting are offered by the toolbar and by the background menu alike; a pick
  // closes either.
  const handleViewPick = (next: FolderView) => {
    setBackgroundMenuAt(null);
    setViewMenuAnchor(null);
    changeView(next);
  };

  const handleSortPick = (key: FolderSortKey) => {
    setBackgroundMenuAt(null);
    setSortMenuAnchor(null);
    setPickedSort(key);
  };

  const folderRows = !isProjectsPage ? folderRowsOf(folder) : [];

  const projectRows = !isProjectsPage
    ? projectRowsOf(folder)
    : (!isSearchPending ? projects?.map(projectRowOf) ?? [] : []);

  const rows = [...folderRows, ...projectRows];

  if (sort) rows.sort((a, b) => compareItems(sort, currentLanguage, a, b));

  // Each kind of item under its own name, in the order sorted above: the folders other teams shared
  // first, then the teams' own folders, so a hundred folders around them do not hide either, then
  // the rest of the folders, then the projects.
  const sections: FolderSection[] = [
    { key: 'shared', title: t('Folder_Shared_Chip'), rows: rows.filter((row) => row.isSharedWithMe) },
    { key: 'teams', title: t('Common_Teams'), rows: rows.filter((row) => row.isTeamRoot) },
    {
      key: 'folders',
      title: t('Common_Folders'),
      rows: rows.filter((row) => row.type === 'Folder' && !row.isTeamRoot && !row.isSharedWithMe)
    },
    { key: 'projects', title: t('Common_Projects'), rows: rows.filter((row) => row.type === 'Project') },
  ].filter((section) => section.rows.length > 0);

  const open = Boolean(menuAnchor);
  const isSelectedTeamRoot = Boolean(selectedItem?.isTeamRoot);
  // The Clipboard API is absent on insecure origins, such as a server reached over plain http.
  const canCopyLink = Boolean(navigator.clipboard);

  // A right-click opens the menu at the pointer; the keyboard's menu key under the item, right edges aligned.
  const menuPosition = menuAnchor && 'point' in menuAnchor
    ? { anchorReference: 'anchorPosition' as const, anchorPosition: menuAnchor.point }
    : {
      anchorEl: menuAnchor?.element,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' } as const,
      transformOrigin: { vertical: 'top', horizontal: 'right' } as const,
    };

  // A refused action stays in the menu, greyed out with the reason on hover. Only a refused one
  // is wrapped: the menu's arrow keys skip an element without a tabindex, which the wrapper is.
  const actionItem = (label: string, icon: React.ReactNode, allowed: boolean, onClick: () => void, danger = false) => {
    const item = (
      <MenuItem key={label} onClick={onClick} disabled={!allowed} sx={danger ? [menuItemStyles, menuDangerItemStyles] : menuItemStyles}>
        <ListItemIcon>{icon}</ListItemIcon>
        {label}
      </MenuItem>
    );

    return allowed
      ? item
      : <Tooltip key={label} title={disabledActionTooltip} placement="left"><div>{item}</div></Tooltip>;
  };

  // On the line under the search box rather than above the items, so they do not move down while it shows.
  const loadingBar = (isFetching || isAddProjectLoading) && <LinearProgress sx={loadingBarStyles} />;

  // A folder that is not there, or a search that found nothing, says so. An empty folder says
  // nothing: its count reads 0 and the pane stays empty. A `div` and not Typography's `<p>`, whose
  // bottom margin `index.css` takes away, which left the message on the pane's bottom edge.
  const emptyMessage = isFolderMissing
    ? <Typography component="div" sx={emptyMessageStyles}>{t('Folder_NotFound')}</Typography>
    : isProjectsPage && projects && !isFetching && rows.length === 0 &&
      <Typography component="div" sx={emptyMessageStyles}>{t('Folder_Search_Empty')}</Typography>;

  // A choice of how the listing is shown: a check marks the one in force.
  const optionItem = (label: string, selected: boolean, onClick: () => void) => (
    <MenuItem key={label} role="menuitemradio" aria-checked={selected} onClick={onClick} sx={menuItemStyles}>
      <ListItemIcon>{selected ? <CheckIcon size={16} /> : <Box sx={{ width: 16 }} />}</ListItemIcon>
      <Box sx={{ flexGrow: 1 }}>{label}</Box>
    </MenuItem>
  );

  const viewItems = [
    optionItem(t('Folder_View_Grid'), view === 'grid', () => handleViewPick('grid')),
    optionItem(t('Folder_View_List'), view === 'list', () => handleViewPick('list')),
  ];

  const sortOptions: { key: FolderSortKey, label: string }[] = [
    { key: 'name', label: t('Common_Name') },
    { key: 'size', label: t('Common_Size') },
    { key: 'dateCreated', label: t('Common_DateCreated') },
    { key: 'dateModified', label: t('Common_DateModified') },
  ];

  const sortItems = sortOptions.map(({ key, label }) => optionItem(label, sort === key, () => handleSortPick(key)));

  return (
    <Box sx={containerStyles}>
      <Header />
      {/* A file manager's window: a toolbar, and under it the tree beside the items. */}
      <Paper sx={windowStyles}>
        <Box sx={toolbarStyles}>
          {/* How the items are shown, and refreshing them, always there, search results included. */}
          <Box sx={toolbarGroupStyles}>
            <Button
              sx={toolbarButtonStyles}
              startIcon={<ArrowDownUpIcon size={16} />}
              endIcon={<ChevronDownIcon size={14} />}
              aria-haspopup="menu"
              onClick={(event) => setSortMenuAnchor(event.currentTarget)}
            >
              {t('Folder_SortBy')}
            </Button>
            <Button
              sx={toolbarButtonStyles}
              startIcon={view === 'grid' ? <LayoutGridIcon size={16} /> : <ListIcon size={16} />}
              endIcon={<ChevronDownIcon size={14} />}
              aria-haspopup="menu"
              onClick={(event) => setViewMenuAnchor(event.currentTarget)}
            >
              {t('Folder_ViewAs')}
            </Button>
            <IconButton
              sx={toolbarIconButtonStyles}
              disabled={isFetching}
              onClick={!isProjectsPage ? refreshFolders : projectsRefetch}
            >
              <RefreshCwIcon size={16} />
            </IconButton>
          </Box>
          {/* What can be added to the folder in view, for a reader who may add it. Search results
              are no folder, so there both stand disabled rather than leave a gap. */}
          <Box sx={toolbarGroupStyles}>
            {(isProjectsPage || canCreateFolder) &&
              <Button
                sx={toolbarButtonStyles}
                startIcon={<FolderPlusIcon size={16} />}
                disabled={Boolean(isProjectsPage)}
                onClick={() => setFolderModalOpen(true)}
              >
                {t('Folder_New')}
              </Button>}
            {(isProjectsPage || canUploadProject) &&
              <Button
                sx={toolbarButtonStyles}
                startIcon={<UploadIcon size={16} />}
                disabled={Boolean(isProjectsPage)}
                onClick={() => setUploadModalOpen(true)}
              >
                {t('Common_Upload')}
              </Button>}
          </Box>
        </Box>
        <Box sx={windowBodyStyles}>
          <Box sx={treePaneStyles}>
            {/* Search results come from all over the tree, so no folder of it is the one in view. */}
            <FolderTreePane
              currentPath={!isProjectsPage ? viewedPath : undefined}
              activeId={open ? selectedItem?.id : undefined}
              // Search results have no order of their own to lend the tree, which keeps to names then.
              sort={sort ?? 'name'}
              onMenu={actionMenuOpen} />
          </Box>
          <Box sx={contentPaneStyles}>
            <Box sx={contentHeadStyles}>
              <SearchInput
                variant="plain"
                text={search}
                placeholder={t('Folder_Search')}
                action={(value) => { setSearchParams({ search: value }); }}
              />
              {(isProjectsPage ? projects : folder) &&
                <Typography sx={contentCountStyles}>{t('Folder_ItemCount', { count: rows.length })}</Typography>}
              {loadingBar}
            </Box>
            <Box sx={contentScrollStyles} onContextMenu={backgroundMenuOpen}>
              {/* Only with something to show: an empty grid keeps its padding and an empty table its
                  head, and either pushed the message below the middle of the pane. */}
              {sections.length > 0 && (view === 'grid'
                ? <FolderGrid
                  sections={sections}
                  activeId={open ? selectedItem?.id : undefined}
                  showLocation={Boolean(isProjectsPage)}
                  onOpen={openItem}
                  onMenu={actionMenuOpen} />
                : <FolderTable
                  sections={sections}
                  activeId={open ? selectedItem?.id : undefined}
                  showLocation={Boolean(isProjectsPage)}
                  sort={sort}
                  onSort={setPickedSort}
                  onOpen={openItem}
                  onMenu={actionMenuOpen} />)}
              {emptyMessage}
            </Box>
          </Box>
        </Box>
      </Paper>
      <ProjectUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        uploadProject={async (formData: FormData) => { await addProject(formData); }}
        folder={folder}
        onBulkUploadRequested={() => {
          setUploadModalOpen(false);
          setBulkUploadModalOpen(true);
        }} />
      <BulkUploadModal
        open={bulkUploadModalOpen}
        onClose={() => setBulkUploadModalOpen(false)}
        parentFolder={folder} />
      <FolderModal
        isOpen={folderModalOpen}
        onClose={handleFolderCanceled}
        folderItem={selectedItem}
        // A new name is checked against the folders beside it: a new folder's are the folder in view's,
        // a renamed one's are those of the folder it is in. One another team shared sits at the root
        // only for this reader, beside folders that are not its siblings, so the server checks it.
        currentFolder={selectedItem ? (selectedItem.isSharedWithMe ? null : selectedParent) : folder}
        onRenamed={handleRenamed} />
      <MoveDialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        item={moveItem}
        onMoved={handleMoved} />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCanceled}
        onConfirm={handleDeleteConfirmation}
        warningSubtitle={selectedItem?.type === 'Folder' && selectedItem.item.children.length
          ? t('Folder_Delete_WarningSubtitle', { count: selectedItem.item.children.length })
          : undefined} />
      <Menu
        id="row-menu"
        {...menuPosition}
        // Focus goes to the menu rather than its first entry: a refused entry is wrapped for its
        // tooltip, and the wrapper, taken for the first entry, left the arrow keys nowhere to go.
        variant="menu"
        disableAutoFocusItem
        // Focus is not handed back as the menu closes: it would take it from the dialog an entry opens.
        disableRestoreFocus
        open={open}
        onClose={actionMenuClose}
        onContextMenu={closeOnRightClick}
        slotProps={{ paper: { sx: menuPaperStyles } }}
      >
        {actionItem(t('Common_Open'), <ArrowRightIcon size={16} />, true, handleOpen)}
        {actionItem(t('Folder_OpenInNewTab'), <SquareArrowOutUpRightIcon size={16} />, true, handleOpenInNewTab)}
        {/* A team root folder is managed on the Teams page: here it can only be opened or linked to. */}
        {!isSelectedTeamRoot && [
          <Divider key="edit" />,
          // A project's name comes from its file, so only folders can be renamed here.
          selectedItem?.type === 'Folder' && actionItem(t('Common_Rename'), <PencilIcon size={16} />, canEditSelected, handleRename),
          actionItem(t('Folder_Move_Action'), <FolderInputIcon size={16} />, canEditSelected, handleMove),
          selectedItem?.type === 'Folder' && actionItem(t('Folder_Share'), <Share2Icon size={16} />, canShareSelected, handleShare),
        ]}
        {canCopyLink && [
          <Divider key="link" />,
          actionItem(t('Folder_CopyLink'), <LinkIcon size={16} />, true, handleCopyLink),
        ]}
        {!isSelectedTeamRoot && [
          <Divider key="delete" />,
          actionItem(t('Common_Delete'), <Trash2Icon size={16} />, canDeleteSelected, handleDelete, true),
        ]}
      </Menu>
      <Menu
        id="folder-menu"
        anchorReference="anchorPosition"
        anchorPosition={backgroundMenuAt ?? undefined}
        variant="menu"
        disableAutoFocusItem
        // Focus handed back as the menu fades out would take it from the dialog its entry opens.
        disableRestoreFocus
        open={Boolean(backgroundMenuAt)}
        onClose={() => setBackgroundMenuAt(null)}
        onContextMenu={closeOnRightClick}
        slotProps={{ paper: { sx: menuPaperStyles } }}
      >
        {/* Search results are no folder to add to. */}
        {!isProjectsPage && [
          actionItem(t('Folder_New'), <FolderPlusIcon size={16} />, canCreateFolder, handleNewFolder),
          actionItem(t('Common_Upload'), <FileUpIcon size={16} />, canUploadProject, handleUpload),
          <Divider key="view" />,
        ]}
        <ListSubheader sx={menuSubheaderStyles}>{t('Folder_ViewAs')}</ListSubheader>
        {viewItems}
        <Divider />
        <ListSubheader sx={menuSubheaderStyles}>{t('Folder_SortBy')}</ListSubheader>
        {sortItems}
      </Menu>
      <Menu
        id="sort-menu"
        anchorEl={sortMenuAnchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        variant="menu"
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
        slotProps={{ paper: { sx: menuPaperStyles } }}
      >
        {sortItems}
      </Menu>
      <Menu
        id="view-menu"
        anchorEl={viewMenuAnchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        variant="menu"
        open={Boolean(viewMenuAnchor)}
        onClose={() => setViewMenuAnchor(null)}
        slotProps={{ paper: { sx: menuPaperStyles } }}
      >
        {viewItems}
      </Menu>
      <ShareDialog
        open={canShareSelected && shareDialogOpen}
        onClose={handleShareCanceled}
        folder={shareTargetFolder} />
    </Box >
  );
};

const containerStyles = {
  display: 'flex',
  flexDirection: 'column' as const,
  width: '100%',
};

export default ProjectsPage;
